import { cloneDeep } from 'lodash';
import { defineStore } from 'pinia';
import { makeDataState, makeDataStateGetters, withStateLoading } from '@/util/dataState';
import { taggedLogger } from '@/util/taggedLogger';
import { OrganisationFeature } from '@/stores/organisationProfile/types';
import type {
    OrganisationFeatureSet,
    OrganisationProfileState,
} from '@/stores/organisationProfile/types';
import { client } from '@/api/http';
import { type AxiosResponse, HttpStatusCode } from 'axios';

const logger = taggedLogger('organisation-profile');

const initialFeatureSet: OrganisationFeatureSet = {
    [OrganisationFeature.OrderSurgicalGuide]: false,
    [OrganisationFeature.HipSpine]: false,
};

export const useOrganisationProfile = defineStore('organisation-profile', {
    state: (): OrganisationProfileState => ({
        features: cloneDeep(initialFeatureSet),
        ...makeDataState(),
    }),
    getters: {
        ...makeDataStateGetters(),
    },
    actions: {
        async load(orgId: number): Promise<void> {
            await withStateLoading(this, async () => {
                const profile = await _getOrganisationProfile(orgId);
                profile.features.forEach((feature) => {
                    const key = feature.name as OrganisationFeature;
                    if (key in this.features) {
                        this.features[key] = true;
                    }
                });
            });
        },
        clear(): void {
            logger.debug('Clearing current organisation profile information...');
            this.$reset();
        },
    },
});

interface _ApiOrganisationProfileObject {
    features: { id: number; name: string }[];
}

async function _getOrganisationProfile(orgId: number): Promise<_ApiOrganisationProfileObject> {
    return client
        .get(`/organisation/${orgId}/profile`)
        .then(({ status, data }: AxiosResponse<_ApiOrganisationProfileObject>) => {
            if (status === HttpStatusCode.Ok) {
                return data;
            }
            throw Error(`Failed to load organisation profile: ${orgId}`);
        });
}
